import {
  Component,
  inject,
  OnInit,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { ServiceOffering, ServiceOfferingControllerService } from '../../../core/generated';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AvailabilityService } from '../../../shared/services/availability.service';
import { EnumDescriptionPipe } from '../../../shared/pipes/enum-description.pipe';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { ProfileProviderPJ } from '../../../core/generated';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { ContainerComponent } from '../../../shared/container/container.component';

@Component({
    selector: 'app-service-offering-provider-pj',
    standalone: true,
    imports: [CommonModule, MaterialModule, RouterLink, EnumDescriptionPipe, AvailabilityComponent, ContainerComponent],
    templateUrl: './service-offering-provider-pj.component.html',
    styleUrl: './service-offering-provider-pj.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceOfferingProviderPJComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly serviceOfferingControllerService = inject(ServiceOfferingControllerService);
  private readonly profilePJContext = inject(ProviderPJContextService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly availabilityService = inject(AvailabilityService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  serviceOfferingProviderPJList: ServiceOffering[] = [];
  providerPJ: ProfileProviderPJ | undefined;
  isLoading = false;
  
  readonly isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);

  private currentCnpj: string | undefined;

  ngOnInit(): void {
    this.setupContextSubscription();
    this.initialize();
  }

  private setupContextSubscription(): void {
    this.profilePJContext.providerPJ$.pipe(
      filter((providerPJ): providerPJ is ProfileProviderPJ => providerPJ !== null),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((providerPJ) => {
      this.handleProviderPJChange(providerPJ);
    });
  }

  private handleProviderPJChange(providerPJ: ProfileProviderPJ): void {
    const hasChanged = !this.providerPJ || this.providerPJ.id !== providerPJ.id;
    
    if (hasChanged) {
      this.providerPJ = providerPJ;
      this.serviceOfferingProviderPJList = [];
      
      if (providerPJ.cnpj) {
        this.loadServiceOffering();
      }
      
      this.cdr.markForCheck();
    }
  }

  async initialize(): Promise<void> {
    await this.profilePJContext.ensureContextFromUrlIfMissing();

    const context = this.profilePJContext.getProviderPJ();

    if (context) {
      this.providerPJ = context;

      if (context.cnpj && this.serviceOfferingProviderPJList.length === 0) {
        this.loadServiceOffering();
      }
    } else {
      this.redirectToProvider();
    }
  }

  private redirectToProvider(): void {
    const providerPJId = this.route.parent?.snapshot.paramMap.get('id');
    if (providerPJId) {
      this.router.navigate([`/providerPJ/${providerPJId}`]);
    }
  }

  loadServiceOffering(): void {
    if (!this.providerPJ?.cnpj) {
      return;
    }

    if (this.isLoading || this.currentCnpj === this.providerPJ.cnpj) {
      return;
    }

    this.isLoading = true;
    this.currentCnpj = this.providerPJ.cnpj;

    this.serviceOfferingControllerService
      .findByCnpj(this.providerPJ.cnpj)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.serviceOfferingProviderPJList = response || [];

          if (this.serviceOfferingProviderPJList.length === 0) {
            this.navigateToServiceOfferingForm();
          }
          
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading service offerings:', error);
          this.serviceOfferingProviderPJList = [];
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private navigateToServiceOfferingForm(): void {
    const id = this.route.parent?.snapshot.paramMap.get('id');
    if (id) {
      this.router.navigate([`providerPJ/${id}/serviceOffering/serviceOfferingForm`]);
    }
  }

  negotiations(id: string): void {
    // TODO: Implement negotiations logic
  }

  getAvailabilityDescription(availability: number[]): string[] {
    return this.availabilityService.getAvailabilityDescription(availability);
  }

  publish(id: string): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceOfferingControllerService
      .publish(Number(id), { publicationDate: '' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.currentCnpj = undefined;
          this.isLoading = false;
          this.loadServiceOffering();
        },
        error: (error) => {
          console.error('Error publishing service offering:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  deleteServiceOffering(id: string): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.serviceOfferingControllerService
      .deleteById1(Number(id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.currentCnpj = undefined;
          this.isLoading = false;
          this.loadServiceOffering();
        },
        error: (error) => {
          console.error('Error deleting service offering:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  trackByServiceOffering(index: number, item: ServiceOffering): string | number {
    return item.id || index;
  }
}
